import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserMenu from '../components/UserMenu';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import axios from 'axios';
import {
  getEstudios,
  getLaboratorios,
  crearEstudio,
  actualizarEstudio,
  EstudioOption,
  EstudioRequest,
  LaboratorioOption,
} from '../api/analisisApi';
import { ApiError } from '../types/auth.types';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import Modal from '../components/Modal';
import styles from './AdminPage.module.css';

type Mode = 'list' | 'create' | 'edit';

function criterioTexto(e: EstudioOption): string {
  if (e.minimo != null && e.maximo != null) return `${e.minimo}-${e.maximo}`;
  if (e.minimo != null) return `Desde ${e.minimo}`;
  if (e.maximo != null) return `Hasta ${e.maximo}`;
  if (e.criterio) return e.criterio;
  return '—';
}

const EMPTY_FORM: EstudioRequest = {
  codigo: '',
  descripcion: '',
  unidadMedida: '',
  frecuente: false,
  metodo: '',
  formatoPdf: '',
  criterio: '',
  minimo: null,
  maximo: null,
  laboratorioId: null,
};

export default function EstudiosPage() {
  const navigate = useNavigate();
  const [estudios, setEstudios] = useState<EstudioOption[]>([]);
  const [laboratorios, setLaboratorios] = useState<LaboratorioOption[]>([]);
  const [mode, setMode] = useState<Mode>('list');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<EstudioRequest>(EMPTY_FORM);
  const [filter, setFilter] = useState('');
  const [selectedLabIds, setSelectedLabIds] = useState<Set<number>>(new Set([1]));
  const [labFilterOpen, setLabFilterOpen] = useState(false);
  const labFilterRef = useRef<HTMLDivElement>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!labFilterOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (labFilterRef.current && !labFilterRef.current.contains(e.target as Node)) {
        setLabFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [labFilterOpen]);

  function toggleLabFilter(id: number) {
    setSelectedLabIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    Promise.all([getEstudios(), getLaboratorios()])
      .then(([ests, labs]) => {
        setEstudios(ests);
        setLaboratorios(labs);
      })
      .finally(() => setLoadingData(false));
  }, []);

  const defaultLaboratorioId =
    laboratorios.find((l) => l.nombre.trim().toLowerCase() === 'sagrado corazón')?.id
    ?? laboratorios[0]?.id
    ?? null;

  function set(field: Partial<EstudioRequest>) {
    setForm((prev) => ({ ...prev, ...field }));
  }

  function openCreate() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, laboratorioId: defaultLaboratorioId });
    setError(null);
    setMode('create');
  }

  function openEdit(e: EstudioOption) {
    setEditingId(e.id);
    setForm({
      codigo: e.codigo,
      descripcion: e.descripcion,
      unidadMedida: e.unidadMedida ?? '',
      frecuente: e.frecuente,
      metodo: e.metodo ?? '',
      formatoPdf: e.formatoPdf ?? '',
      criterio: e.criterio ?? '',
      minimo: e.minimo,
      maximo: e.maximo,
      laboratorioId: e.laboratorioId ?? defaultLaboratorioId,
    });
    setError(null);
    setMode('edit');
  }

  function cancel() {
    setMode('list');
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.codigo.trim()) { setError('El código es obligatorio.'); return; }
    if (!form.descripcion.trim()) { setError('La descripción es obligatoria.'); return; }
    setError(null);
    setLoading(true);
    try {
      if (mode === 'create') {
        const nuevo = await crearEstudio(form);
        setEstudios((prev) => [...prev, nuevo].sort((a, b) => {
          if (a.frecuente !== b.frecuente) return a.frecuente ? -1 : 1;
          return a.descripcion.localeCompare(b.descripcion);
        }));
      } else if (editingId !== null) {
        const actualizado = await actualizarEstudio(editingId, form);
        setEstudios((prev) => prev.map((e) => (e.id === actualizado.id ? actualizado : e)));
      }
      setMode('list');
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'Error al guardar.');
      } else {
        setError('Error al guardar.');
      }
    } finally {
      setLoading(false);
    }
  }

  const estudiosDeLaboratoriosSeleccionados = estudios.filter((e) => {
    const labKey = e.laboratorioId ?? 0;
    return selectedLabIds.size === 0 || selectedLabIds.has(labKey);
  });

  const filteredEstudios = estudiosDeLaboratoriosSeleccionados.filter((e) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return e.codigo.toLowerCase().includes(q) || e.descripcion.toLowerCase().includes(q);
  });

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>← Volver</button>
        <img src={logo} alt="Logo" className={styles.topbarLogo} />
        <p className={styles.topbarTitle}>Estudios</p>
        <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        {loadingData ? (
          <div className={styles.spinnerWrapper}>
            <div className={styles.spinner} />
            <p className={styles.spinnerText}>Cargando estudios...</p>
          </div>
        ) : (
        <>
        {mode === 'list' && (
          <div className={`${styles.listActions} ${styles.listActionsWithTitle}`}>
            <div className={styles.titleWithFilter}>
              <h2 className={styles.listTitle}>
                Total
                <span className={styles.titleBadge}>{estudiosDeLaboratoriosSeleccionados.length}</span>
              </h2>
              <div className={styles.labFilterWrapper} ref={labFilterRef}>
                <button
                  type="button"
                  className={styles.labFilterBtn}
                  onClick={() => setLabFilterOpen((o) => !o)}
                >
                  Laboratorio{selectedLabIds.size > 0 ? ` (${selectedLabIds.size})` : ''} ▾
                </button>
                {labFilterOpen && (
                  <div className={styles.labFilterPanel}>
                    {laboratorios.map((l) => (
                      <label key={l.id} className={styles.labFilterOption}>
                        <input
                          type="checkbox"
                          checked={selectedLabIds.has(l.id)}
                          onChange={() => toggleLabFilter(l.id)}
                        />
                        {l.nombre}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button className={styles.newBtnContent} onClick={openCreate}>+ Nuevo estudio</button>
          </div>
        )}
        {/* ── Formulario ── */}
        <Modal
          isOpen={mode !== 'list'}
          onClose={cancel}
          title={mode === 'create' ? 'Nuevo estudio' : 'Editar estudio'}
          width={880}
          minHeight={520}
          disableClose={loading}
        >
          {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.formRow}>
              <div className={styles.field}>
                <label className={styles.label}>Código <span className={styles.required}>*</span></label>
                <input className={styles.input} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} placeholder="Ej: CREAT" autoFocus />
              </div>
              <div className={`${styles.field} ${styles.fieldGrow2}`}>
                <label className={styles.label}>Descripción <span className={styles.required}>*</span></label>
                <input className={styles.input} value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} placeholder="Nombre del estudio" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Unidad de medida</label>
                <input className={styles.input} value={form.unidadMedida} onChange={(e) => set({ unidadMedida: e.target.value })} placeholder="Ej: mg/dl" />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.field}>
                <label className={styles.label}>Mínimo</label>
                <input className={styles.input} type="number" value={form.minimo ?? ''} onChange={(e) => set({ minimo: e.target.value ? Number(e.target.value) : null })} placeholder="0.0" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Máximo</label>
                <input className={styles.input} type="number" value={form.maximo ?? ''} onChange={(e) => set({ maximo: e.target.value ? Number(e.target.value) : null })} placeholder="0.0" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Método</label>
                <input className={styles.input} value={form.metodo} onChange={(e) => set({ metodo: e.target.value })} placeholder="Método de análisis" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Laboratorio</label>
                <select className={styles.select} value={form.laboratorioId ?? ''} onChange={(e) => set({ laboratorioId: e.target.value ? Number(e.target.value) : null })}>
                  {laboratorios.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </div>
              <div className={`${styles.field} ${styles.fieldCheckbox}`}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" checked={form.frecuente} onChange={(e) => set({ frecuente: e.target.checked })} />
                  Estudio frecuente
                </label>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.field}>
                <label className={styles.label}>Criterio</label>
                <textarea className={styles.textarea} value={form.criterio} onChange={(e) => set({ criterio: e.target.value })} placeholder="Criterios de interpretación" rows={7} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Formato PDF</label>
                <textarea className={styles.textarea} value={form.formatoPdf} onChange={(e) => set({ formatoPdf: e.target.value })} placeholder="Plantilla de texto para el PDF" rows={7} />
              </div>
            </div>
            <div className={styles.formActions}>
              <Button type="submit" loading={loading} loadingText="Guardando...">Guardar</Button>
              <button type="button" className={styles.cancelBtn} onClick={cancel} disabled={loading}>Cancelar</button>
            </div>
          </form>
        </Modal>

        {/* ── Lista ── */}
        {estudios.length === 0 ? (
          <p className={styles.emptyMsg}>No hay estudios registrados.</p>
        ) : (
          <div className={styles.listCard}>
            <div className={styles.listFilter}>
              <input
                className={styles.filterInput}
                type="text"
                placeholder="Filtrar por código o descripción..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <div className={styles.tableBody}>
            <div className={styles.tableHeader}>
              <span>Código</span>
              <span>Descripción</span>
              <span>Criterio</span>
              <span>Unidad</span>
              <span>Frecuente</span>
              <span>Laboratorio</span>
              <span></span>
            </div>
            {filteredEstudios.length === 0 ? (
              <p className={styles.emptyMsg}>No se encontraron estudios.</p>
            ) : filteredEstudios.map((e, idx) => (
              <div key={e.id} className={`${styles.tableRow} ${idx % 2 === 0 ? styles.listRowEven : ''}`}>
                <span className={styles.cellCode}>{e.codigo}</span>
                <span className={styles.cellDesc}>{e.descripcion}</span>
                <span className={`${styles.cellSecondary} ${styles.cellEllipsis}`} title={criterioTexto(e)}>{criterioTexto(e)}</span>
                <span className={styles.cellSecondary}>{e.unidadMedida ?? '—'}</span>
                <span>
                  {e.frecuente
                    ? <span className={styles.badgeYes}>Sí</span>
                    : <span className={styles.badgeNo}>No</span>}
                </span>
                <span className={styles.cellSecondary}>{e.laboratorioNombre ?? '—'}</span>
                <button className={styles.editBtn} onClick={() => openEdit(e)}>Editar</button>
              </div>
            ))}
            </div>{/* fin tableBody */}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
